<template>
	<Pupup
		v-if="popup.show && popup.brand"
		@close="handleClose"
		:scrollTop="pupupScrollTop"
		:showClose="true"
		:width="popup.width || 1200"
		:shuffleUp="popup.shuffleUp"
		:hasBaseButtons="hasBaseButtons"
		:loading="popup.loading"
		key="brand-card-popup">
		<template v-slot:header v-if="headerTabs">
			<Tabs
				:tabs="headerTabs"
				:key="headerTabsKey"
				tabStyle="modal"
				background="#eff3f2"
				:startTab="headerStartTab"
				:noSave="true"
				@tabChange="handleHeaderTabChange" />
		</template>
		<template v-slot:content>
			<div class="pupup-content-wrap">
				<BrandCard
					:brand="popup.brand"
					:dateOfInterestThumb="popup.dateOfInterestThumb"
					:dateOfInterestLabel="popup.dateOfInterestLabel"
					:dateOfInterestItems="popup.dateOfInterestItems"
					:currentUserDateOfInterestIds="popup.currentUserDateOfInterestIds"
					:matchBrand="popup.userBrand"
					:buttonDefaults="popup.buttonDefaults"
					:fields="popup.fields"
					:full="true"
					:useHeaderTabs="!!headerTabs"
					:showConnectWindowOverride="showConnectWindow"
					:showConnectedInfoOverride="showConnectedInfo"
					:displayMode="popup.displayMode || 'default'"
					class="brand"
					@viewMessage="handleViewMessage"
					@reportBrand="handleReportBrand"
					@action="handleAction"
					@scrollTop="incrementScrollTop"
					@update:showConnectWindow="updateShowConnectWindow"
					@update:showConnectedInfo="updateShowConnectedInfo"
					@crownInfo="handleCrownInfo"
					@toggleShowInfo="handleToggleShowInfo"
					@subscribe="handleSubscribe" />
			</div>
		</template>
		<template v-slot:footer-inner v-if="showFooterButtons">
			<BrandActionButtons :buttonDefaults="popup.buttonDefaults" :brand="popup.brand" :singleBrand="true" @action="handleAction" />
		</template>
	</Pupup>
</template>

<script>
import { ref, computed, watch, getCurrentInstance } from "vue";
import Pupup from "./../Display/Pupup.vue";
import BrandCard from "./../Display/BrandCard.vue";
import BrandActionButtons from "./../Display/BrandActionButtons.vue";
import Tabs from "./../Display/Tabs.vue";
import { isSupportedBrandCardPopupSource } from "../../utils/popupSources";

export default {
	name: "BrandCardPopup",
	components: {
		Pupup,
		BrandCard,
		BrandActionButtons,
		Tabs,
	},
	setup() {
		const vm = getCurrentInstance().proxy;
		const store = vm.$store;
		const pupupScrollTop = ref(0);
		const showConnectWindow = ref(true);
		const showConnectedInfo = ref(true);

		const popup = computed(() => store.state.general.brandCardPopup || {});
		const source = computed(() => popup.value.source || "brands");
		const isSupportedSource = () => isSupportedBrandCardPopupSource(source.value);
		const emitPopupEvent = (type, payload) => {
			store.commit("general/setBrandCardPopupEvent", { type, payload, source: source.value, createdAt: Date.now() });
		};
		const emitPopupEventIfSupported = (type, payload) => {
			if (!isSupportedSource()) return;
			emitPopupEvent(type, payload);
		};
		const shouldShowActionButtons = (brand) => {
			if (!brand) return false;
			return brand.status !== "connected" && (brand.status !== "requested" || brand.requestType === "sent");
		};

		const hasBaseButtons = computed(() => {
			if (popup.value.hideActionButtons) return false;
			return shouldShowActionButtons(popup.value.brand);
		});

		const showFooterButtons = computed(() => {
			if (popup.value.hideActionButtons) return false;
			return shouldShowActionButtons(popup.value.brand);
		});

		const headerTabs = computed(() => {
			const brand = popup.value.brand;
			if (!brand) return null;
			if (brand.status === "connected") {
				return [
					{
						label: "Contact " + (brand.brand_name || "this brand"),
						value: true,
						field: "showConnectedInfo",
					},
					{
						label: "All about " + (brand.brand_name || "this brand"),
						value: false,
						field: "showConnectedInfo",
					},
				];
			}
			if (brand.status === "requested" && brand.requestType !== "sent") {
				return [
					{
						label: "Connecting with " + (brand.brand_name || "this brand"),
						value: true,
						field: "showConnectWindow",
					},
					{
						label: "All about " + (brand.brand_name || "this brand"),
						value: false,
						field: "showConnectWindow",
					},
				];
			}
			return null;
		});

		const headerStartTab = computed(() => {
			if (!headerTabs.value || !popup.value.brand) return 0;
			if (popup.value.brand.status === "connected") {
				return showConnectedInfo.value ? 0 : 1;
			}
			return showConnectWindow.value ? 0 : 1;
		});

		const headerTabsKey = computed(() => {
			const brandId = popup.value.brand?.brand_id || "none";
			return `brand-card-popup-tabs-${brandId}-${headerStartTab.value}`;
		});

		const resetHeaderState = () => {
			showConnectWindow.value = true;
			showConnectedInfo.value = true;
		};

		const handleClose = () => {
			if (isSupportedSource()) {
				emitPopupEvent("close");
			}
			store.commit("general/closeBrandCardPopup");
		};

		const handleAction = (payload) => {
			if (!isSupportedSource()) return;
			store.commit("general/setBrandActionEvent", {
				type: "actioner",
				payload: { brandId: payload?.brandId, action: payload?.action, forceIt: payload?.forceIt },
				source: source.value,
				createdAt: Date.now(),
			});
		};

		const handleViewMessage = (payload) => {
			emitPopupEventIfSupported("viewMessage", payload);
		};

		const handleReportBrand = (payload) => {
			emitPopupEventIfSupported("reportBrand", payload);
		};

		const handleCrownInfo = (payload) => {
			emitPopupEventIfSupported("crownInfo", payload);
		};

		const handleToggleShowInfo = (payload) => {
			emitPopupEventIfSupported("toggleShowInfo", payload);
		};

		const handleSubscribe = (payload) => {
			emitPopupEventIfSupported("subscribe", payload);
		};

		const handleHeaderTabChange = ({ tab }) => {
			if (tab.field === "showConnectWindow") {
				showConnectWindow.value = tab.value;
			}
			if (tab.field === "showConnectedInfo") {
				showConnectedInfo.value = tab.value;
			}
		};

		const updateShowConnectWindow = (value) => {
			showConnectWindow.value = value;
		};

		const updateShowConnectedInfo = (value) => {
			showConnectedInfo.value = value;
		};

		const incrementScrollTop = () => {
			pupupScrollTop.value += 1;
		};

		watch(
			() => popup.value.show,
			(newVal) => {
				if (newVal) {
					pupupScrollTop.value += 1;
					// Track metadata for feedback widget
					if (popup.value.brand?.brand_id) {
						store.commit("feedback/setMetadata", {
							key: "viewingBrandId",
							value: popup.value.brand.brand_id,
						});
						store.commit("feedback/setOpenWindow", "BrandCardPopup");
					}
				} else {
					// Clear metadata when closing
					store.commit("feedback/clearMetadata", "viewingBrandId");
					store.commit("feedback/removeOpenWindow", "BrandCardPopup");
				}
			}
		);

		watch(
			() => popup.value.brand,
			(newBrand, oldBrand) => {
				// Only reset tabs when viewing a different brand, not on object refresh
				if (newBrand?.brand_id !== oldBrand?.brand_id) {
					resetHeaderState();
				}
				pupupScrollTop.value += 1;
				// Update metadata when brand changes
				if (newBrand?.brand_id && popup.value.show) {
					store.commit("feedback/setMetadata", {
						key: "viewingBrandId",
						value: newBrand.brand_id,
					});
				}
			}
		);

		return {
			popup,
			pupupScrollTop,
			headerTabs,
			headerStartTab,
			headerTabsKey,
			showConnectWindow,
			showConnectedInfo,
			hasBaseButtons,
			showFooterButtons,
			handleClose,
			handleAction,
			handleViewMessage,
			handleReportBrand,
			handleCrownInfo,
			handleToggleShowInfo,
			handleSubscribe,
			handleHeaderTabChange,
			updateShowConnectWindow,
			updateShowConnectedInfo,
			incrementScrollTop,
		};
	},
};
</script>